import { toast } from "react-toastify";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { buildLoginCredentialsMessage } from "./loginCredentials";

// Returns an international WhatsApp number (digits only), or null if invalid.
export const normalizeWhatsAppNumber = (
  mobileNumber: unknown,
  countryCode?: unknown,
): string | null => {
  let phoneNumber = String(mobileNumber || "").replace(/\D/g, "");
  if (phoneNumber.startsWith("00")) phoneNumber = phoneNumber.slice(2);
  if (/^0\d{10}$/.test(phoneNumber)) phoneNumber = phoneNumber.slice(1);
  if (phoneNumber.length === 10) {
    const code = String(countryCode || "91").replace(/\D/g, "").replace(/^00/, "");
    phoneNumber = `${code || "91"}${phoneNumber}`;
  }
  return /^[1-9]\d{6,14}$/.test(phoneNumber) ? phoneNumber : null;
};

export const buildWhatsAppChatUrl = (phoneNumber: string | null, message: string) =>
  `${phoneNumber ? `https://wa.me/${phoneNumber}` : "https://wa.me/"}?text=${encodeURIComponent(message)}`;

// Opens WhatsApp with a prefilled message; the user still has to press send.
export const openWhatsAppChat = (phoneNumber: string | null, message: string) => {
  window.open(buildWhatsAppChatUrl(phoneNumber, message), "_blank", "noopener,noreferrer");
};

const isAndroidBrowser = () =>
  !Capacitor.isNativePlatform() &&
  typeof navigator !== "undefined" &&
  /Android/i.test(navigator.userAgent);

const buildWhatsAppSendQuery = (phoneNumber: string | null, message: string) =>
  `${phoneNumber ? `phone=${phoneNumber}&` : ""}text=${encodeURIComponent(message)}`;

// Desktop/iOS browsers need a window opened during the click, before any await.
export const shouldPreopenWhatsAppWindow = () =>
  !Capacitor.isNativePlatform() && !isAndroidBrowser();

// Capacitor Android hands whatsapp:// to the OS. If nothing takes focus away
// from the app (WhatsApp not installed), fall back to the web URL.
const launchDeepLinkWithFallback = (deepLink: string, fallbackUrl: string) => {
  let leftApp = false;
  const markLeft = () => {
    if (document.visibilityState === "hidden") leftApp = true;
  };
  document.addEventListener("visibilitychange", markLeft);
  const pauseListener = App.addListener("pause", () => {
    leftApp = true;
  });

  window.location.href = deepLink;

  window.setTimeout(() => {
    document.removeEventListener("visibilitychange", markLeft);
    void pauseListener.then((handle) => handle.remove());
    if (!leftApp) {
      window.open(fallbackUrl, "_blank", "noopener,noreferrer");
    }
  }, 1500);
};

// Opens the recipient's WhatsApp chat directly where the platform allows it.
export const openWhatsAppChatDirect = (
  phoneNumber: string | null,
  message: string,
  preopenedWindow: Window | null = null,
) => {
  const webUrl = buildWhatsAppChatUrl(phoneNumber, message);
  const query = buildWhatsAppSendQuery(phoneNumber, message);

  if (Capacitor.getPlatform() === "android") {
    launchDeepLinkWithFallback(`whatsapp://send?${query}`, webUrl);
    return;
  }

  if (isAndroidBrowser()) {
    // Chrome opens the app (WhatsApp or WhatsApp Business) and uses the
    // fallback URL itself when no app can handle the intent.
    window.location.href = `intent://send?${query}#Intent;scheme=whatsapp;S.browser_fallback_url=${encodeURIComponent(
      webUrl,
    )};end`;
    return;
  }

  // Desktop and other browsers: existing WhatsApp web behaviour.
  if (preopenedWindow) {
    preopenedWindow.opener = null;
    preopenedWindow.location.href = webUrl;
  } else {
    window.open(webUrl, "_blank", "noopener,noreferrer");
  }
};

type CredentialsProfile = {
  fullName?: string;
  martialId?: string;
  maritalId?: string;
  password?: string;
  mobileNumber?: string;
  countryCode?: string;
};

// Same behaviour as the Full Profile Manage WhatsApp button.
export const sendLoginCredentialsOnWhatsApp = (
  profile: CredentialsProfile,
  websiteUrl?: string,
  websiteLinks?: unknown,
) => {
  const phoneNumber = normalizeWhatsAppNumber(profile.mobileNumber, profile.countryCode);
  if (!phoneNumber) {
    toast.error("A valid phone number is missing for this profile.");
    return;
  }
  try {
    const message = buildLoginCredentialsMessage({
      fullName: profile.fullName || "",
      martialId: profile.martialId || profile.maritalId || "",
      password: profile.password || "",
      websiteUrl,
      websiteLinks,
    });
    openWhatsAppChat(phoneNumber, message);
  } catch (error) {
    toast.error(error instanceof Error ? error.message : "Unable to prepare login credentials.");
  }
};

// English + Telugu in one message, with a single public profile link at the end.
export const buildSuitableMatchMessage = ({
  bureauName,
  recipientName,
  recipientGender,
  senderName,
  senderGender,
  profileUrl,
}: {
  bureauName?: string;
  recipientName?: string;
  recipientGender?: string;
  senderName?: string;
  senderGender?: string;
  profileUrl: string;
}): string => {
  const bureau = String(bureauName || "").trim() || "MeeMarriage";
  const recipient = String(recipientName || "").trim();
  const sender = String(senderName || "").trim();
  const title = (gender?: string) => {
    const normalized = String(gender || "").trim().toLowerCase();
    return normalized === "male" ? "Mr. " : normalized === "female" ? "Ms. " : "";
  };
  const gender = String(senderGender || "").trim().toLowerCase();
  const pronoun = gender === "female" ? "She" : gender === "male" ? "He" : "They";
  const possessive = gender === "female" ? "her" : gender === "male" ? "his" : "their";
  const isAwaiting = gender === "male" || gender === "female" ? "is" : "are";

  return [
    `🌸 Welcome to ${bureau} 🌸`,
    "",
    `Greetings! ${title(recipientGender)}${recipient},`,
    "",
    `${title(senderGender)}${sender}...`,
    "",
    "...has recently viewed your profile.",
    "",
    "...has liked your profile.",
    "",
    `Your profile matches ${possessive} preferences for a life partner.`,
    "",
    `${pronoun} ${isAwaiting} showing interest in your profile.`,
    "",
    `${pronoun} would like to know more details about you.`,
    "",
    `${pronoun} ${isAwaiting} awaiting your response.`,
    "",
    `Please take a look at ${possessive} full profile, and if you are interested in further conversation, do get in touch immediately.`,
    "",
    "100% verified profile. Don't miss this profile.",
    "",
    "",
    `🌸 ${bureau} కు స్వాగతం 🌸`,
    "",
    `నమస్కారం ${recipient} గారు,`,
    "",
    `${sender} గారు`,
    "",
    "మీ ప్రొఫైల్‌ను ఇప్పుడే వీక్షించారు",
    "మీ ప్రొఫైల్ నీ లైక్ చేశారు",
    "",
    "మీ ప్రొఫైల్ తన జీవిత భాగస్వామి ప్రాధాన్యతలకు",
    "సరిపోలే విధంగా ఉంది",
    "",
    "మీ ప్రొఫైల్‌పై తను ఆసక్తి చూపుతున్నారు",
    "",
    "మీ గురించి మరిన్ని వివరాలు తెలుసుకోవాలని",
    "అనుకుంటున్నారు",
    "",
    "మీ సమాధానం కోసం ఎదురుచూస్తున్నారు",
    "",
    "మీరు కూడా తన ఫుల్ ప్రొఫైల్‌ను ఒకసారి చూసి,",
    "మరింత సంభాషించడానికి ఆసక్తి ఉంటే వెంటనే కాంటాక్ట్ అవ్వండి",
    "",
    "100% ధృవీకరించబడిన ప్రొఫైల్స్; ఈ ప్రొఫైల్‌ను మిస్ అవ్వకండి.",
    "",
    "🔗 View Full Profile / పూర్తి ప్రొఫైల్ చూడండి:",
    profileUrl,
  ].join("\n");
};
